import { useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { IPaginator } from "common/types";
import { Optional } from "common/utils/types";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import PillButton from "client/common/components/buttons/PillButton";

import { PRODUCT_FILTER_DEFINITIONS } from "../constants/filterDefinitions";
import { ProductSearchFilters, SelectedProductFilters } from "../types";
import { getSearchString } from "../utils";
import AppliedFilterTags from "./AppliedFilterTags";
import FilterPanelModal from "./FilterPanelModal";

import styles from "../styles/ProductCardListHeader.module.less";

type ProductCardListHeaderProps = {
  paginator: IPaginator;
  isRefetching: boolean;
  /** Pluralized in the count, e.g. "deal" reads "Showing 1 - 14 of 14 deals". */
  entityName: string;
  filterOptions: Optional<ProductSearchFilters>;
  filters: SelectedProductFilters;
  onChange: (search: Partial<SelectedProductFilters>) => void;
  /** Whether the page offers filtering; false renders the result count alone. */
  showFilters?: boolean;
};

type FilterActionsProps = Pick<
  ProductCardListHeaderProps,
  "filterOptions" | "filters" | "onChange"
>;

const FilterActions = ({ filterOptions, filters, onChange }: FilterActionsProps) => {
  const [isFiltering, setIsFiltering] = useState(false);

  return (
    <div className={styles.actions}>
      {filterOptions && (
        <AppliedFilterTags<SelectedProductFilters, ProductSearchFilters>
          definitions={PRODUCT_FILTER_DEFINITIONS}
          options={filterOptions}
          value={filters}
          onChange={onChange}
        />
      )}
      <PillButton
        variant="secondary"
        size="s"
        type="button"
        onClick={() => setIsFiltering(true)}
        disabled={!filterOptions}
      >
        <span className={styles.filter}>
          <Icon icon={faFilter} /> Filter
        </span>
      </PillButton>
      {filterOptions && (
        <FilterPanelModal
          definitions={PRODUCT_FILTER_DEFINITIONS}
          options={filterOptions}
          isVisible={isFiltering}
          onClose={() => setIsFiltering(false)}
          value={filters}
          onChange={onChange}
        />
      )}
    </div>
  );
};

/** The bar above a ProductCardList: result count, active filter chips, and the filter modal. */
const ProductCardListHeader = ({
  paginator,
  isRefetching,
  entityName,
  filterOptions,
  filters,
  onChange,
  showFilters = true,
}: ProductCardListHeaderProps) => (
  <div className={styles.header}>
    <div className={styles.label}>
      {isRefetching ? <Spinner /> : getSearchString(paginator, entityName)}
    </div>
    {showFilters && (
      <FilterActions filterOptions={filterOptions} filters={filters} onChange={onChange} />
    )}
  </div>
);

export default ProductCardListHeader;
